import { useState, useEffect, useRef } from "react";
import { Card, CardBody, CardHeader } from "../card/card";
import { v4 as uuidv4 } from "uuid"; // For generating unique IDs
import CONFIG from "../../config/config";
import "./option-card.css";
import useKeyboardShortcut from "../../hooks/useKeyboardShortcut";
import { getAuthHeaders } from "../../misc.js";

const OptionCard = ({
  option,
  onNameChange,
  onImageSelect,
  onPromptChange,
  onGoogleQueryChange, // New callback prop for Google Query changes
  uniqueId,
  onImagesGenerated,
  optionImageAspectRatio,
  label,
  shortcutModifier,
  dimensions = { width: 1024, height: 768 },
}) => {
  const [googlePage, setGooglePage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [generatingImages, setGeneratingImages] = useState([]); // Track generating AI images
  const [error, setError] = useState(null); // Track errors
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false); // Loading state for Google images
  const [isLoadingAI, setIsLoadingAI] = useState(false); // Loading state for AI images
  const [hoveredImage, setHoveredImage] = useState(null); // Track hovered image for tooltip
  const prevImagesLengthRef = useRef(option.images?.length);

  // Determine if a single image is provided
  const singleImageUrl = option.image && option.image.image;
  const hasMultipleImages = option.images && option.images.length > 0;

  // Handle pagination when images are added or removed (only applies to multiple images)
  useEffect(() => {
    if (!hasMultipleImages) return;
    if(singleImageUrl){
      onImageSelect(option.image);
    }
    const prevImagesLength = prevImagesLengthRef.current;
    const currentImagesLength = option.images?.length;

    if (currentImagesLength > prevImagesLength) {
      // New images added
      if (prevImagesLength === 0) {
        // First generation
        setCurrentPage(1);
      } else {
        const totalPages = Math.ceil(currentImagesLength / 20);
        setCurrentPage(totalPages);
      }
    } else if (currentImagesLength < prevImagesLength) {
      // Images removed
      const totalPages = Math.ceil(currentImagesLength / 20);
      if (currentPage > totalPages) {
        setCurrentPage(totalPages > 0 ? totalPages : 1);
      }
    }

    prevImagesLengthRef.current = currentImagesLength;
  }, [option.images?.length, currentPage, hasMultipleImages, onImageSelect, singleImageUrl]);

  // Handle image selection
  const handleImageClick = (image) => {
    onImageSelect(image); // Directly set the selection to the clicked image
  };

  // Updated handleGenerateImages function with token attached to the requests
  const handleGenerateImages = async (type) => {
    setError(null); // Reset error state

    if (type === "ai") {
      setIsLoadingAI(true); // Set loading state for AI images
      // Generate a unique placeholder for the AI image being generated
      const placeholderId = uuidv4();
      setGeneratingImages((prev) => [
        ...prev,
        { imageId: placeholderId, status: "generating" },
      ]);

      try {
        // Enqueue the AI image generation task via the backend endpoint
        const response = await fetch(`${CONFIG.API_URL}/generate/ai-images`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            prompt: option.prompt,
            dimensions,
            timeout: 300000,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to generate AI images");
        }

        const data = await response.json();

        // Flatten images if they are returned as nested arrays
        let images = data.images;
        if (Array.isArray(images) && images.length > 0 && Array.isArray(images[0])) {
          images = images.flat();
        }

        // If images are returned, add them to the parent component's images
        if (images && images.length > 0) {
          onImagesGenerated(images);
        }

        // Remove the placeholder
        setGeneratingImages((prev) =>
          prev.filter((img) => img.imageId !== placeholderId)
        );
      } catch (error) {
        console.error("Error generating AI images:", error);
        setError("Failed to generate AI images. Please try again.");
        // Remove the placeholder on error
        setGeneratingImages((prev) =>
          prev.filter((img) => img.imageId !== placeholderId)
        );
      } finally {
        setIsLoadingAI(false); // Reset the loading state
      }
    } else if (type === "google") {
      setIsLoadingGoogle(true); // Set loading state for Google images

      try {
        const response = await fetch(`${CONFIG.API_URL}/generate/google-images`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            // Use option.query if available; otherwise, fall back to option.text
            prompt: (option.query || option.text) + ` ${dimensions.width}x${dimensions.height}`,
            aspectRatio: optionImageAspectRatio,
            page: googlePage,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to generate Google images");
        }

        const data = await response.json();

        if (data.images && data.images.length > 0) {
          onImagesGenerated(data.images);
          setGooglePage((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Error generating Google images:", error);
        setError("Failed to generate Google images. Please try again.");
      } finally {
        setIsLoadingGoogle(false); // Reset the loading state
      }
    }
  };

  // Keyboard shortcuts for generating images
  useKeyboardShortcut({
    modifier: shortcutModifier,
    keyTrigger: "g",
    handler: () => handleGenerateImages("google")
  });
  useKeyboardShortcut({
    modifier: shortcutModifier,
    keyTrigger: "a",
    handler: () => handleGenerateImages("ai")
  });

  // Calculate paginated images for multiple images (if applicable)
  const startIndex = (currentPage - 1) * 20;
  const endIndex = startIndex + 20;
  const paginatedImages = hasMultipleImages
    ? option.images.slice(startIndex, endIndex)
    : [];
  const totalPages = hasMultipleImages ? Math.ceil(option.images.length / 20) : 1;

  return (
    <Card>
      <CardHeader>
        <h5>{label}</h5>
      </CardHeader>
      <CardBody>
        <label className="form-label">Name</label>
        <input
          type="text"
          className="form-control mb-1"
          value={option.text}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={!option.text}
          placeholder={option.text ? "Enter Name" : "Not Required for this quiz type"}
        />
        <label className="form-label">AI Prompt</label>
        <input
          type="text"
          className="form-control mb-1"
          value={option.prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          disabled={!option.prompt}
          placeholder={option.prompt ? "Enter AI Prompt" : "Not Required for this quiz type"}
        />
        <label className="form-label">Google Images Query</label>
        <input
          type="text"
          className="form-control mb-1"
          // If no query is provided, default to the value of option.text
          value={option.query || option.text}
          disabled={!option.query || !option.text}
          onChange={(e) => onGoogleQueryChange(e.target.value)}
          placeholder={(option.prompt || option.text) ? "Enter Google Query" : "Not Required for this quiz type"}
        />

        {/* Error message */}
        {error && <div className="alert alert-danger mb-2">{error}</div>}

        <div className="mb-1 d-grid gap-2 d-md-flex">
          <button
            className="btn btn-success flex-grow-1"
            onClick={() => handleGenerateImages("ai")}
            disabled={isLoadingAI}
          >
            {isLoadingAI ? "Loading..." : "Generate AI"}
          </button>
          <button
            className="btn btn-warning flex-grow-1"
            onClick={() => handleGenerateImages("google")}
            disabled={isLoadingGoogle}
          >
            {isLoadingGoogle ? "Loading..." : "Get Google Images"}
          </button>
        </div>

        <div className="accordion" id={`accordion-${uniqueId}`}>
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${
                  !singleImageUrl && !hasMultipleImages ? "text-center" : ""
                }`}
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#collapse-${uniqueId}`}
              >
                {!singleImageUrl && !hasMultipleImages
                  ? "No Images"
                  : "Images"}
              </button>
            </h2>
            <div
              id={`collapse-${uniqueId}`}
              className={`accordion-collapse collapse ${
                singleImageUrl || hasMultipleImages ? "show" : ""
              }`}
            >
              <div className="accordion-body">
                {singleImageUrl ? (
                  // Render the single image
                  <div className="text-center">
                    <img
                      src={singleImageUrl}
                      alt="option"
                      className={`image-item ${
                        option.selection && option.selection.image === singleImageUrl
                          ? "selected"
                          : ""
                      }`}
                      style={{ maxWidth: "100%", height: "auto" }}
                      onClick={() => handleImageClick(option.image)}
                    />
                  </div>
                ) : hasMultipleImages ? (
                  // Render multiple images in a grid
                  <div>
                    <div className="image-grid">
                      {/* Show generating AI image placeholders */}
                      {generatingImages.map((img) => (
                        <div key={img.imageId} className="image-placeholder">
                          <p>Generating AI Image...</p>
                        </div>
                      ))}

                      {/* Show paginated images */}
                      {paginatedImages.map((image, idx) => {
                        return (
                          <div
                            key={idx}
                            className="image-item-container"
                            onMouseEnter={() => setHoveredImage(image)}
                            onMouseLeave={() => setHoveredImage(null)}
                          >
                            <img
                              src={image.preview || image.image}
                              alt="option"
                              className={`image-item ${
                                option.selection === image ? "selected" : ""
                              }`}
                              onClick={() => handleImageClick(image)}
                            />
                            {hoveredImage === image && (
                              <div className="image-tooltip">
                                <img
                                  src={image.image}
                                  alt="option-full"
                                  className="image-tooltip-img"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-1">
                        <button
                          className="btn btn-primary"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <span>
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          className="btn btn-primary"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  // No images available
                  <div className="text-center">
                    <p className="mb-0">
                      Please generate images using the buttons above
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default OptionCard;
